import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import moment from 'moment';

interface PmOfferState {
  id: string;
  accountManagerId: string;
  accountSaleId: string;
  projectAuthorId: string;
  companyId: string;
  number: string;
  date: string;
  dateStart: string;
  dateEnd: string;
  enumPmOfferContractType: any;
  enumPmOfferContractTypeId: any;
  enumPmOfferStatus: any;
  enumPmOfferStatusId: any;
  metadata: any;
  remarks: any;
  accountManager: any;
  projectAuthor: any;
  accountSale: any;
  company: any;
  pmOfferIntervals: any[];
  pmOfferWorkPlaceItems: any[];
  pmOfferLaborExtras: any[];
  pmOfferCommentsTab: any[];
  pmOfferSectionFiles: any[];
  location: any[];
  equipmentTab: {
    [key: string]: any;
  };
  laborTab: {
    [key: string]: any;
  };
  props: {
    [key: string]: any;
  };
}

const currentDate = moment().format('YYYY-MM-DD');
const currentDateEnd = moment().add(1, 'year').format('YYYY-MM-DD');

const initialState: PmOfferState = {
  id: '',
  accountManagerId: '',
  accountSaleId: '',
  projectAuthorId: '',
  companyId: '',
  enumPmOfferContractTypeId: 0,
  enumPmOfferStatusId: 0,
  number: '',
  date: currentDate,
  dateStart: currentDate,
  dateEnd: currentDateEnd,
  remarks: '',
  metadata: null,
  accountManager: null,
  projectAuthor: null,
  accountSale: null,
  company: null,
  enumPmOfferContractType: null,
  enumPmOfferStatus: null,
  pmOfferIntervals: [],
  pmOfferWorkPlaceItems: [],
  pmOfferLaborExtras: [],
  pmOfferCommentsTab: [],
  pmOfferSectionFiles: [],
  location: [],
  equipmentTab: { workPlaceId: null },
  laborTab: { laborPriceOnHour: 300 },
  props: { searchTerm: '', skip: 1, take: 10, totalItems: 0 },
};

const offerSlice = createSlice({
  name: 'pmOffer',
  initialState,
  reducers: {
    setPmOffer: (state, action: PayloadAction<Partial<PmOfferState>>) => {
      return { ...state, ...action.payload };
    },
    updateField: (state: any, action: PayloadAction<{ field: any; value: any }>) => {
      const { field, value } = action.payload;
      state[field] = value;
    },
    updateEquipmentTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.equipmentTab = {
        ...state.equipmentTab,
        [field]: value,
      };
    },
    updateLaborTab: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.laborTab = {
        ...state.laborTab,
        [field]: value,
      };
    },
    addPmOfferWorkPlaceItem: (state, action) => {
      state.pmOfferWorkPlaceItems.push(action.payload);
    },
    updatePmOfferWorkPlaceItem: (state, action: PayloadAction<{ index: number; updates: any }>) => {
      const { index, updates } = action.payload;
      if (state.pmOfferWorkPlaceItems[index]) {
        state.pmOfferWorkPlaceItems[index] = {
          ...state.pmOfferWorkPlaceItems[index],
          ...updates,
        };
      }
    },
    removePmOfferWorkPlaceItem: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmOfferWorkPlaceItems.length) {
        state.pmOfferWorkPlaceItems.splice(index, 1);
      }
    },

    // **Add interval actions**
    addPmOfferInterval: (state, action: PayloadAction<any>) => {
      state.pmOfferIntervals.push(action.payload);
    },
    updatePmOfferInterval: (state, action: PayloadAction<{ index: number; updates: Partial<any> }>) => {
      const { index, updates } = action.payload;
      if (state.pmOfferIntervals[index]) {
        state.pmOfferIntervals[index] = {
          ...state.pmOfferIntervals[index],
          ...updates,
        };
      }
    },
    removePmOfferInterval: (state, action: PayloadAction<number>) => {
      const index = action.payload;
      if (index >= 0 && index < state.pmOfferIntervals.length) {
        state.pmOfferIntervals.splice(index, 1);
      }
    },
    //
    addPmOfferLaborExtra: (state, action) => {
      state.pmOfferLaborExtras.push(action.payload);
    },
    addPmOfferComment: (state, action: PayloadAction<any>) => {
      state.pmOfferCommentsTab.push(action.payload);
    },
    updatePmOfferComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        updates: Partial<any>;
      }>
    ) => {
      const { departmentName, updates } = action.payload;
      const index = state.pmOfferCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (index !== -1) {
        state.pmOfferCommentsTab[index] = {
          ...state.pmOfferCommentsTab[index],
          ...updates,
        };
      } else {
        state.pmOfferCommentsTab.push({
          departmentName,
          ...updates,
        });
      }
    },
    removePmOfferComment: (
      state,
      action: PayloadAction<{
        departmentName: string;
        fileUid: string;
      }>
    ) => {
      const { departmentName, fileUid } = action.payload;
      const commentIndex = state.pmOfferCommentsTab.findIndex((comment) => comment.departmentName === departmentName);

      if (commentIndex !== -1) {
        const comment = state.pmOfferCommentsTab[commentIndex];

        const updatedFiles = (comment.pmOfferCommentFiles || []).filter(
          (file: any) => file.uid !== fileUid && file.id !== fileUid
        );

        state.pmOfferCommentsTab[commentIndex] = {
          ...comment,
          pmOfferCommentFiles: updatedFiles,
        };
      }
    },
    updateProps: (state, action: PayloadAction<{ field: string; value: any }>) => {
      const { field, value } = action.payload;
      state.props = {
        ...state.props,
        [field]: value,
      };
    },
  },
});

export const {
  setPmOffer,
  updateField,
  updateEquipmentTab,
  updateLaborTab,
  addPmOfferWorkPlaceItem,
  updatePmOfferWorkPlaceItem,
  removePmOfferWorkPlaceItem,
  updatePmOfferInterval,
  addPmOfferLaborExtra,
  addPmOfferComment,
  updatePmOfferComment,
  removePmOfferComment,
  updateProps,
} = offerSlice.actions;

export default offerSlice.reducer;
